import {
  CancelOutlined,
  CheckCircleOutlined,
  CommentOutlined,
  DescriptionOutlined,
  ExpandMore,
  LocalOfferOutlined,
  ReceiptLongOutlined,
} from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { JsonViewer } from 'components/Common/JsonViewer'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useAppController } from 'customHooks/useAppController'
import { bomItemSelector } from 'features/BillOfMaterials/store/selectors/bomItemSelector'
import { useBomItemThumbnailImageSrc } from 'features/BillOfMaterials/useBomItemThumbnailImageSrc'
import { ConfirmationDialog } from 'features/ConfirmationDialog/ConfirmationDialog'
import { ProjectQuotationsController } from 'features/RequestForQuotations/Quotation/QuotationResponse/ProjectQuotationController'
import { QuotationsController } from 'features/RequestForQuotations/Quotations/QuotationsController'
import { isEqual } from 'lodash'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import {
  AssemblyHeaderRow,
  BomItemType,
  PartTypeRow,
} from 'model/Project/BoMItemRow'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  MoneyDto,
  QuotationDto,
  RequestForQuotationDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { RootAppState, useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { useShowException } from 'utils/useShowException'

const useStyles = makeStyles({ name: 'RFQResponse' })((theme) => ({
  root: {
    marginTop: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: `calc(${theme.shape.borderRadius}px * 2)`,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  totalsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2, 0),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  statCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.25),
    backgroundColor: theme.palette.background.default,
  },
  detailItem: {
    minWidth: 0,
  },
  sectionHeader: {
    padding: theme.spacing(1.5, 2, 0),
  },
  sectionBody: {
    padding: theme.spacing(1, 2, 1.5),
  },
  sectionGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: theme.spacing(1, 2),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  detailLabel: {
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  commentBox: {
    margin: theme.spacing(0, 2, 2),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  tableWrap: {
    padding: theme.spacing(0, 2, 2),
    overflowX: 'auto' as const,
  },
  itemCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 220,
  },
  thumbnail: {
    width: 36,
    height: 36,
    objectFit: 'contain' as const,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  activitiesCell: {
    display: 'flex',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap' as const,
    justifyContent: 'flex-start',
    minWidth: 120,
  },
  rawAccordion: {
    borderTop: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },
  },
  detailsToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.25, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    userSelect: 'none' as const,
    backgroundColor: theme.palette.background.default,
  },
  detailsToggleIcon: {
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  detailsToggleIconOpen: {
    transform: 'rotate(180deg)',
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      '& > button': {
        flex: 1,
      },
    },
  },
  completedNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  centeredState: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}))

type RFQResponseProps = {
  quotationResponseId: string
  rfqId: string
  bomItemsAndActivities?: RequestForQuotationDto['bomItemsAndActivities']
  onActionComplete?: () => Promise<void> | void
}

type RfqBomItem = NonNullable<
  RequestForQuotationDto['bomItemsAndActivities']
>[number]

function formatMoney(money: MoneyDto | null | undefined): string {
  if (!money?.value && money?.value !== 0) {
    return '-'
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: money.currency || 'EUR',
    }).format(money.value)
  } catch {
    return `${money.value} ${money.currency || ''}`.trim()
  }
}

function formatDate(value: Date | string | undefined): string {
  if (!value) {
    return '-'
  }

  const date = moment(value)
  return date.isValid() ? date.format('LL') : '-'
}

function getStateColor(
  state: string | undefined,
): 'default' | 'success' | 'warning' | 'error' {
  const normalizedState = state?.toLowerCase() || ''

  if (normalizedState.includes('accept')) {
    return 'success'
  }

  if (
    normalizedState.includes('cancel') ||
    normalizedState.includes('declin') ||
    normalizedState.includes('reject')
  ) {
    return 'error'
  }

  if (
    normalizedState.includes('draft') ||
    normalizedState.includes('pending')
  ) {
    return 'warning'
  }

  return 'default'
}

function formatQuotationState(state: string | undefined): string {
  switch (state) {
    case 'New':
      return 'Ready for review'
    case 'Cancelled':
      return 'Cancelled'
    case 'QuotationResponseAcceptedByBuyer':
      return 'Accepted by buyer'
    case 'QuotationResponseCreatedBySupplier':
      return 'Created by supplier'
    case 'QuotationResponseRejectedByBuyer':
      return 'Rejected by buyer'
    case 'QuotationResponseMergedIntoProject':
      return 'Merged into project'
    case 'QuotationResponseMergeError':
      return 'Merge error'
    case 'QuotationResponseReceivedByBuyer':
      return 'Received by buyer'
    default:
      return state || 'Unknown state'
  }
}

function canDecideQuotationResponse(state: string | undefined): boolean {
  return (
    state === 'New' ||
    state === 'QuotationResponseCreatedBySupplier' ||
    state === 'QuotationResponseReceivedByBuyer'
  )
}

function getCompletedStateMessage(state: string | undefined): string {
  switch (state) {
    case 'QuotationResponseAcceptedByBuyer':
      return 'This supplier response has already been accepted.'
    case 'QuotationResponseRejectedByBuyer':
      return 'This supplier response has already been rejected.'
    case 'Cancelled':
      return 'This supplier response was cancelled.'
    case 'QuotationResponseMergedIntoProject':
      return 'This supplier response has already been merged into the project.'
    default:
      return 'No buyer action is available for this supplier response.'
  }
}

function formatVatRatio(value: QuotationDto['vatRatio'] | undefined): string {
  if (!value?.value && value?.value !== 0) {
    return '-'
  }

  const code =
    value.unit || value.quantityType
      ? ` ${value.unit || value.quantityType}`
      : ''
  return `${value.value}${code}`
}

function isDisplayValue(value: string): boolean {
  return Boolean(value && value !== '-')
}

function getBomItemType(
  state: RootAppState,
  bomItemId: string | undefined,
): BomItemType | null {
  if (!bomItemId) {
    return null
  }

  if (state.project.partTypes?.[bomItemId]) {
    return BomItemType.partType
  }

  if (state.project.partInstances?.[bomItemId]) {
    return BomItemType.partInstance
  }

  if (state.project.assemblyHeaders?.[bomItemId]) {
    return BomItemType.assemblyType
  }

  if (state.project.assemblyInstances?.[bomItemId]) {
    return BomItemType.assemblyInstance
  }

  return null
}

function QuotedLineItemCell({
  lineName,
  rfqItem,
  classes,
}: {
  lineName: string | undefined
  rfqItem: RfqBomItem | undefined
  classes: ReturnType<typeof useStyles>['classes']
}) {
  const bomItemType = useAppSelector((state: RootAppState) =>
    getBomItemType(state, rfqItem?.bomItemId),
  )

  const bomItemPointer = useMemo<BomItemPointer | null>(() => {
    if (!rfqItem?.bomItemId || !bomItemType) {
      return null
    }

    return {
      id: rfqItem.bomItemId,
      type: bomItemType,
    }
  }, [bomItemType, rfqItem?.bomItemId])

  const bomItem = useAppSelector((state: RootAppState) => {
    if (!bomItemPointer) {
      return null
    }

    return bomItemSelector(state, bomItemPointer)
  }, isEqual) as AssemblyHeaderRow | PartTypeRow | null

  const thumbnailSrc = useBomItemThumbnailImageSrc(
    bomItemPointer || { id: '', type: BomItemType.partType },
  )

  const displayName =
    lineName || bomItem?.name || bomItem?.originalFileName || '-'

  return (
    <Box className={classes.itemCell}>
      {thumbnailSrc.imageSrc && (
        <img
          src={thumbnailSrc.imageSrc}
          alt={displayName}
          className={classes.thumbnail}
        />
      )}
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {displayName}
      </Typography>
    </Box>
  )
}

export const RFQResponse = ({
  quotationResponseId,
  rfqId,
  bomItemsAndActivities,
  onActionComplete,
}: RFQResponseProps) => {
  const { classes } = useStyles()
  const ShowException = useShowException('Request for Quotations')

  const { controller } = useAppController(() => {
    return new QuotationsController()
  })

  const { controller: projectQuotationsController, loading } = useAppController(
    () => {
      return new ProjectQuotationsController()
    },
  )

  const [quotationResponse, setQuotationResponse] =
    useState<QuotationDto | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [confirmAction, setConfirmAction] = useState<
    'accept' | 'decline' | null
  >(null)
  const [actionFeedback, setActionFeedback] = useState<string | null>(null)
  const [detailsExpanded, setDetailsExpanded] = useState(false)
  const [quotedLinesExpanded, setQuotedLinesExpanded] = useState(false)

  const fetchQuotationResponse = useCallback(async () => {
    setIsFetching(true)

    try {
      const resp = await controller.getQuotations({
        id: quotationResponseId,
      } as any)

      setQuotationResponse(resp.details?.[0] ?? null)
    } catch (err) {
      ShowException(err)
    } finally {
      setIsFetching(false)
    }
  }, [ShowException, controller, quotationResponseId])

  const lineCount = quotationResponse?.lines?.length || 0
  const totalQuotedQuantity = useMemo(
    () =>
      (quotationResponse?.lines ?? []).reduce(
        (sum, line) => sum + (line.quantity || 0),
        0,
      ),
    [quotationResponse?.lines],
  )

  const isActionLoading = Boolean(
    loading[`AcceptQuotationResponsePost-${rfqId}-${quotationResponseId}`] ||
    loading[`CancelQuotationResponsePost-${rfqId}-${quotationResponseId}`],
  )
  const canDecide = canDecideQuotationResponse(quotationResponse?.state)

  const overviewItems = useMemo(
    () =>
      [
        {
          label: 'Reference',
          value:
            quotationResponse?.reference ||
            quotationResponse?.orderNumber ||
            '-',
        },
        { label: 'Order number', value: quotationResponse?.orderNumber || '-' },
        {
          label: 'Supplier',
          value: quotationResponse?.sellingPartyName || '-',
        },
        { label: 'Buyer', value: quotationResponse?.buyingPartyName || '-' },
        { label: 'Created', value: formatDate(quotationResponse?.dateCreated) },
        { label: 'Updated', value: formatDate(quotationResponse?.dateChanged) },
      ].filter((item) => isDisplayValue(item.value)),
    [quotationResponse],
  )

  const commercialItems = useMemo(
    () =>
      [
        {
          label: 'Quotation date',
          value: formatDate(quotationResponse?.quotationDate),
        },
        {
          label: 'Valid until',
          value: formatDate(quotationResponse?.quotationExpireDate),
        },
        {
          label: 'Validity',
          value: quotationResponse?.validityPeriodInDays
            ? `${quotationResponse.validityPeriodInDays} days`
            : '-',
        },
        { label: 'Currency', value: quotationResponse?.currencyCode || '-' },
        {
          label: 'Payment terms',
          value: quotationResponse?.paymentTermsCode || '-',
        },
        {
          label: 'Delivery terms',
          value: quotationResponse?.deliveryTermsCode || '-',
        },
        { label: 'Language', value: quotationResponse?.languageCode || '-' },
        {
          label: 'VAT ratio',
          value: formatVatRatio(quotationResponse?.vatRatio),
        },
        {
          label: 'Exchange rate',
          value: quotationResponse?.exchangeRate
            ? quotationResponse.exchangeRate.toString()
            : '-',
        },
      ].filter((item) => isDisplayValue(item.value)),
    [quotationResponse],
  )

  const handleAction = useCallback(
    async (action: 'accept' | 'decline') => {
      try {
        if (action === 'accept') {
          await projectQuotationsController.AcceptQuotationResponsePost(
            rfqId,
            quotationResponseId,
            '',
            '',
          )
          setActionFeedback('Quotation accepted.')
        } else {
          await projectQuotationsController.CancelQuotationResponsePost(
            rfqId,
            quotationResponseId,
            '',
            '',
          )
          setActionFeedback('Quotation declined.')
        }

        await fetchQuotationResponse()
        await onActionComplete?.()
      } catch (err) {
        ShowException(err)
      } finally {
        setConfirmAction(null)
      }
    },
    [
      ShowException,
      fetchQuotationResponse,
      onActionComplete,
      projectQuotationsController,
      quotationResponseId,
      rfqId,
    ],
  )

  useEffect(() => {
    void fetchQuotationResponse()
  }, [fetchQuotationResponse])

  if (isFetching) {
    return (
      <Paper className={classes.root} elevation={0}>
        <div className={classes.centeredState}>
          <CircularProgress size={28} />
        </div>
      </Paper>
    )
  }

  if (!quotationResponse) {
    return (
      <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
        No quotation response details are available for this RFQ yet.
      </Alert>
    )
  }

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.header}>
        <div className={classes.headerText}>
          <span>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {quotationResponse.sellingPartyName ||
                'Selling party name not found'}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Review the received quotation details, then accept or decline the
              response.
            </Typography>
          </span>
          <div className={classes.chipRow}>
            <Chip
              size="small"
              icon={<ReceiptLongOutlined />}
              label={formatQuotationState(quotationResponse.state)}
              color={getStateColor(quotationResponse.state)}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<DescriptionOutlined />}
              label={`${lineCount} quoted line${lineCount === 1 ? '' : 's'}`}
              variant="outlined"
            />
            <Chip
              size="small"
              icon={<LocalOfferOutlined />}
              label={`${totalQuotedQuantity} total quantity`}
              variant="outlined"
            />
          </div>
        </div>

        <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
          <Typography variant="caption" color="textSecondary">
            Gross total
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {formatMoney(quotationResponse.taxInclusiveAmount)}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Net {formatMoney(quotationResponse.taxExclusiveAmount)}
          </Typography>
        </Box>
      </div>
      {actionFeedback && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="success" variant="outlined">
            {actionFeedback}
          </Alert>
        </Box>
      )}
      <div
        className={classes.detailsToggle}
        onClick={() => setDetailsExpanded((prev) => !prev)}
      >
        <ExpandMore
          fontSize="small"
          className={`${classes.detailsToggleIcon} ${
            detailsExpanded ? classes.detailsToggleIconOpen : ''
          }`}
        />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {detailsExpanded ? 'Hide supplier details' : 'Show supplier details'}
        </Typography>
      </div>
      <Collapse in={detailsExpanded}>
        <Divider />
        <div className={classes.totalsGrid}>
          <div className={classes.statCard}>
            <Typography variant="caption" color="textSecondary">
              Tax exclusive
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {formatMoney(quotationResponse.taxExclusiveAmount)}
            </Typography>
          </div>
          <div className={classes.statCard}>
            <Typography variant="caption" color="textSecondary">
              Tax inclusive
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {formatMoney(quotationResponse.taxInclusiveAmount)}
            </Typography>
          </div>
          <div className={classes.statCard}>
            <Typography variant="caption" color="textSecondary">
              Allowances
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {formatMoney(quotationResponse.allowanceTotalAmount)}
            </Typography>
          </div>
          <div className={classes.statCard}>
            <Typography variant="caption" color="textSecondary">
              Charges
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {formatMoney(quotationResponse.chargeTotalAmount)}
            </Typography>
          </div>
        </div>
        <Divider />
        {overviewItems.length > 0 && (
          <>
            <div className={classes.sectionHeader}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Supplier details
              </Typography>
            </div>
            <div className={classes.sectionBody}>
              <div className={classes.sectionGrid}>
                {overviewItems.map((item) => (
                  <div key={item.label} className={classes.detailItem}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      className={classes.detailLabel}
                    >
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.value}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
            <Divider />
          </>
        )}
        {commercialItems.length > 0 && (
          <>
            <div className={classes.sectionHeader}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Terms
              </Typography>
            </div>
            <div className={classes.sectionBody}>
              <div className={classes.sectionGrid}>
                {commercialItems.map((item) => (
                  <div key={item.label} className={classes.detailItem}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      className={classes.detailLabel}
                    >
                      {item.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {item.value}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        {quotationResponse.comment && (
          <>
            <Divider />
            <div className={classes.sectionHeader}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Supplier notes
              </Typography>
            </div>
            <div className={classes.commentBox}>
              <div className={classes.chipRow}>
                <CommentOutlined fontSize="small" color="action" />
                <Typography variant="subtitle2">Comment</Typography>
              </div>
              <Typography
                variant="body2"
                sx={{ mt: 1, whiteSpace: 'pre-wrap' }}
              >
                {quotationResponse.comment}
              </Typography>
            </div>
          </>
        )}
        {import.meta.env.DEV && (
          <Accordion className={classes.rawAccordion} disableGutters>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Full payload
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <JsonViewer data={quotationResponse} />
            </AccordionDetails>
          </Accordion>
        )}
      </Collapse>
      <Divider />
      <div
        className={classes.detailsToggle}
        onClick={() => setQuotedLinesExpanded((prev) => !prev)}
      >
        <ExpandMore
          fontSize="small"
          className={`${classes.detailsToggleIcon} ${
            quotedLinesExpanded ? classes.detailsToggleIconOpen : ''
          }`}
        />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {quotedLinesExpanded ? 'Hide quoted lines' : 'Show quoted lines'}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {lineCount} line{lineCount === 1 ? '' : 's'}
        </Typography>
      </div>
      <Collapse in={quotedLinesExpanded}>
        {lineCount > 0 ? (
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Activities</TableCell>
                  <TableCell>Comment</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(quotationResponse.lines ?? []).map((line, index) => (
                  <TableRow key={line.id} hover>
                    <TableCell>
                      <QuotedLineItemCell
                        lineName={line.name}
                        rfqItem={bomItemsAndActivities?.[index]}
                        classes={classes}
                      />
                    </TableCell>
                    <TableCell>
                      <Box className={classes.activitiesCell}>
                        {(bomItemsAndActivities?.[index]?.activities ?? [])
                          .length > 0 ? (
                          (
                            bomItemsAndActivities?.[index]?.activities ?? []
                          ).map((activity, activityIndex) => (
                            <WorkingStepIconSvg
                              key={`${line.id}-${activity.workingStepType || 'unknown'}-${activityIndex}`}
                              workingStepType={activity.workingStepType}
                              attributes={{ width: '24px' }}
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{line.comment || '-'}</TableCell>
                    <TableCell align="right">{line.quantity || 0}</TableCell>
                    <TableCell align="right">
                      {formatMoney(line.salesPrice)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <Box sx={{ px: 2, pb: 2 }}>
            <Alert severity="info" variant="outlined">
              This response does not include any quoted lines.
            </Alert>
          </Box>
        )}
      </Collapse>
      {canDecide ? (
        <div className={classes.actionBar}>
          <Typography variant="caption" color="textSecondary">
            Accept this quote to proceed with the supplier, or decline it to
            keep the RFQ open for other options.
          </Typography>
          <div className={classes.actionButtons}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelOutlined />}
              disabled={isActionLoading}
              onClick={() => setConfirmAction('decline')}
            >
              Decline quote
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleOutlined />}
              disabled={isActionLoading}
              onClick={() => setConfirmAction('accept')}
            >
              Accept quote
            </Button>
          </div>
        </div>
      ) : (
        <div className={classes.actionBar}>
          <div className={classes.completedNotice}>
            <CheckCircleOutlined color="success" fontSize="small" />
            <Typography variant="caption">
              {getCompletedStateMessage(quotationResponse.state)}
            </Typography>
          </div>
        </div>
      )}
      <ConfirmationDialog
        open={confirmAction !== null}
        onClose={() => setConfirmAction(null)}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() =>
          confirmAction ? handleAction(confirmAction) : Promise.resolve()
        }
        title={
          confirmAction === 'accept'
            ? 'Accept quotation response'
            : 'Decline quotation response'
        }
        message={
          confirmAction === 'accept'
            ? `Accept the quotation response from ${quotationResponse.sellingPartyName || 'this supplier'}?`
            : `Decline the quotation response from ${quotationResponse.sellingPartyName || 'this supplier'}?`
        }
        confirmText={confirmAction === 'accept' ? 'Accept' : 'Decline'}
        cancelText="Go Back"
        disableAutoCloseOnConfirm
        busy={isActionLoading}
      />
    </Paper>
  )
}
